import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import BrandingFooter from '@/components/BrandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SetupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/setup-first-admin`, {
        email,
        password,
        name,
        role: 'admin'
      });
      
      // Store token
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      
      toast.success('Admin account created!');
      
      // Reload the page to reinitialize auth context
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (error) {
      console.error('Setup error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create admin account';
      toast.error(errorMessage);
      
      // If admin already exists, redirect to login
      if (errorMessage.includes('already exists')) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative flex items-center justify-center px-4">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[500px]" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-6">
            <h1 className="plotter-font text-4xl font-bold tracking-tight text-white">
              Plotter
            </h1>
            <p className="text-[#A1A1AA]/70 text-sm font-light mt-2">
              by <a href="https://bingelabs.in/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Bingelabs</a>
            </p>
          </div>
          <p className="text-[#A1A1AA]">First-time setup</p>
          <p className="text-[#52525B] text-sm mt-2">Create your admin account</p>
        </div>

        <div className="glass-card rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  placeholder="John Doe"
                  required
                  data-testid="setup-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  placeholder="admin@example.com"
                  required
                  data-testid="setup-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="setup-password"
                />
              </div>
              <p className="text-xs text-[#52525B] mt-1">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="setup-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Admin Account'
              )}
            </Button>
          </form>
        </div>
      </div>
      <BrandingFooter />
    </div>
  );
}
